/**
 * User Activity Service
 * 
 * Tracks user presence/activity for admin monitoring
 */

const API_URL = process.env.REACT_APP_SIGNATURE_API_URL || 
  'https://r3vefztcpc.execute-api.ap-southeast-2.amazonaws.com/default/store-efs-report-photos-in-s3';

const HEARTBEAT_INTERVAL = 30000; // 30 seconds

let heartbeatTimer = null;
let currentUsername = null;
let currentSection = null;
let currentReferenceNo = null;

/**
 * Start tracking user activity
 * @param {string} username - Current user's username
 * @param {string} referenceNo - Current form reference number
 */
export const startActivityTracking = (username, referenceNo = null) => {
  if (!username) return;
  
  currentUsername = username;
  currentReferenceNo = referenceNo;
  
  // Send immediate heartbeat
  sendHeartbeat();
  
  // Clear any existing timer
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
  }
  
  // Set up periodic heartbeat
  heartbeatTimer = setInterval(() => {
    sendHeartbeat();
  }, HEARTBEAT_INTERVAL);
  
  console.log('[UserActivity] Started tracking for', username);
};

/**
 * Stop tracking user activity
 */
export const stopActivityTracking = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  
  currentUsername = null;
  currentSection = null;
  currentReferenceNo = null;
  
  console.log('[UserActivity] Stopped tracking');
};

/**
 * Update current section (called when user navigates between form steps)
 * @param {string} section - Current section/step name
 */
export const updateCurrentSection = (section) => {
  currentSection = section;
  // Send immediate heartbeat with new section
  sendHeartbeat();
};

/**
 * Update current reference number (called when form state changes)
 * @param {string} referenceNo - Current form reference number
 */
export const updateReferenceNo = (referenceNo) => {
  currentReferenceNo = referenceNo;
};

/**
 * Send heartbeat to backend
 */
const sendHeartbeat = async () => {
  if (!currentUsername || !API_URL) return;
  
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'updateUserActivity',
        username: currentUsername,
        currentSection: currentSection,
        referenceNo: currentReferenceNo
      })
    });
    
    // Don't log errors - heartbeat is non-critical
    if (response.ok) {
      console.debug('[UserActivity] Heartbeat sent');
    }
  } catch (err) {
    // Silent fail - activity tracking is non-critical
    console.debug('[UserActivity] Heartbeat failed:', err.message);
  }
};

/**
 * Force an immediate heartbeat (useful before navigation/close)
 */
export const sendImmediateHeartbeat = () => {
  sendHeartbeat();
};

// Clean up on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    stopActivityTracking();
  });
}
